import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams, useHistory } from 'react-router-dom';
import { Instagram as LoadingState } from 'react-content-loader';
import { useSelector } from 'react-redux';
import { useForm } from 'react-hook-form';
import * as yup from 'yup';

import { selectUser } from 'redux/slices/userSlice';
import { yupResolver } from '@hookform/resolvers';

import { sortGroups } from 'helpers/groupsHelper';
import { RequiredString, String } from 'helpers/yupHelper';

import { getGroupsList } from 'services/rest/Group/getGroupsList';
import { getAmbassadorById } from 'services/rest/Ambassador/getAmbassadorById';
import {
  Ambassador,
  updateAmbassador
} from 'services/rest/Ambassador/updateAmbassador';

import { ErrorAlert, SuccessAlert } from 'components/Alert';

import EditEmployeeForm from './EditEmployeeForm';
import { selectCompany } from 'redux/slices/Company/CompanySlice';

interface GroupParams {
  groupId: string;
  ambassadorId: string;
}

const EditEmployee = () => {
  const { t } = useTranslation(['group_page', 'validations']);
  const userSchema = yup.object().shape({
    name: RequiredString().max(70, t('validations:length_error', { max: 70 })),
    lastName: RequiredString().max(
      70,
      t('validations:length_error', { max: 70 })
    ),
    email: RequiredString()
      .email(t('validations:email'))
      .max(100, t('validations:length_error', { max: 100 })),
    code: String()
      .trim()
      .max(20, t('validations:length_error', { max: 20 }))
  });

  const { register, handleSubmit, errors } = useForm({
    resolver: yupResolver(userSchema)
  });
  const { groupId, ambassadorId } = useParams<GroupParams>();
  const backGroup = `/admin/group/detail/${groupId}`;
  const history = useHistory();
  const { currentUser } = useSelector(selectUser);
  const { company } = useSelector(selectCompany);
  const [employee, setEmployee] = useState<any>({
    groups: {},
    groupsIds: []
  });
  const [groups, setGroups] = useState<{ id: number; name: string }[]>([]);
  const [loading, setLoading] = useState(true);
  const [loader, setLoader] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);
  const [createSuccess, setCreateSuccess] = useState<string | null>(null);
  const [language, setLanguage] = useState<string | null>(null);
  const [phone, setPhone] = useState(employee?.phone);
  const [allowBonus, setAllowBonus] = useState<boolean>(employee?.allowBonus);
  const [allowSuggestedPost, setAllowSuggestedPost] = useState<boolean>(false);

  const getData = async () => {
    try {
      const ambassadorData = await getAmbassadorById(
        currentUser.token,
        ambassadorId
      );
      if (ambassadorData) {
        setEmployee(ambassadorData?.data?.data);
        setPhone(ambassadorData?.data?.data?.phone);
        setAllowBonus(ambassadorData?.data?.data?.allowBonus);
        setAllowSuggestedPost(ambassadorData?.data?.data?.allowSuggestedPost);
        setLanguage(ambassadorData?.data?.data?.language);
      }

      const groupData = await getGroupsList(currentUser?.token, 500, 1);
      if (groupData) {
        setGroups(sortGroups(groupData?.data?.data?.docs));
      }

      setLoading(false);
    } catch (error) {
      window.location.href = '/admin/group';
    }
  };

  const handleCheck = (group: { id: number; name: string }) => {
    const { id, name } = group;
    if (employee?.groupsIds?.includes(id)) {
      const copyGroups = employee?.groups;
      delete copyGroups[id];
      setEmployee({
        ...employee,
        groupsIds: employee?.groupsIds?.filter((emGId: number) => emGId !== id),
        groups: copyGroups
      });

      return;
    }
    setEmployee({
      ...employee,
      groupsIds: [...employee?.groupsIds, id],
      groups: {
        ...employee?.groups,
        [id]: {
          name
        }
      }
    });
  };

  useEffect(() => {
    getData();
  }, []);

  const onSubmit = async (ambassadorData: Ambassador) => {
    setLoader(true);
    ambassadorData.id = parseInt(ambassadorId);
    ambassadorData.language = language ?? company?.language;
    ambassadorData.companyId = company?.id;
    ambassadorData.phone = phone;
    ambassadorData.allowBonus = allowBonus;
    ambassadorData.allowSuggestedPost = allowSuggestedPost;
    ambassadorData.groups = employee?.groups;
    ambassadorData.groupsIds = employee?.groupsIds;
    const { data, errors } = await updateAmbassador(
      currentUser?.token,
      ambassadorData
    );
    if (data) {
      history.push(backGroup, t('group_page:update_ambassador_success'));
    }
    if (errors) {
      setCreateError(errors?.data);
    }
    setLoader(false);
  };

  if (loading) {
    return <LoadingState height="230px" width="100%" />;
  }

  return (
    <>
      {createError && (
        <ErrorAlert
          text={createError}
          allowClose={true}
          onClose={() => setCreateError(null)}
        />
      )}

      {createSuccess && (
        <SuccessAlert
          text={createSuccess}
          allowClose={true}
          onClose={() => setCreateSuccess(null)}
        />
      )}
      <EditEmployeeForm
        employee={employee}
        loading={loading}
        company={company}
        loader={loader}
        groupId={groupId}
        errors={errors}
        handleSubmit={handleSubmit(onSubmit)}
        register={register}
        groups={groups}
        setLanguage={setLanguage}
        language={language}
        phone={phone}
        setPhone={setPhone}
        allowBonus={allowBonus}
        setAllowBonus={setAllowBonus}
        allowSuggestedPost={allowSuggestedPost}
        setAllowSuggestedPost={setAllowSuggestedPost}
        handleCheck={handleCheck}
      />
    </>
  );
};
export default EditEmployee;
